/**
 * IntroPage.jsx
 * LUNA Intro/Landing Page
 * Ported from intro.html (p5.js animation)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sketch from 'react-p5';
import './IntroPage.css';

const CONFIG = {
    text: "LUNA",
    baseParticleCount: 2000, // [Opt] 3000 -> 2000 성능 최적화
    starCount: 300,          // [Opt] 400 -> 300
    mouseRadius: 180,
    starSpeedBase: 0.5,
    starSpeedWarp: 80,
    colors: [
        [0, 255, 255],
        [99, 102, 241],
        [192, 132, 252],
        [255, 255, 255]
    ]
};

export default function IntroPage() {
    const navigate = useNavigate();
    const [isWarping, setIsWarping] = useState(false);

    // p5 related refs
    const particles = useRef([]);
    const stars = useRef([]);
    const mode = useRef('intro');
    const systemInitialized = useRef(false);
    const minX = useRef(0);
    const maxX = useRef(0);

    // Initial setup
    const setup = (p, canvasParentRef) => {
        const canvas = p.createCanvas(p.windowWidth, p.windowHeight);
        canvas.parent(canvasParentRef);
        p.pixelDensity(Math.min(window.devicePixelRatio, 2));

        // [Fix] 폰트 로딩 대기 제거 (블로킹 방지) -> 폴백 폰트로 즉시 렌더링
        setTimeout(() => {
            initSystem(p);
            systemInitialized.current = true;
        }, 300); // 300ms 딜레이 유지
    };

    const windowResized = (p) => {
        p.resizeCanvas(p.windowWidth, p.windowHeight);
        initSystem(p);
    };

    const initSystem = (p) => {
        particles.current = [];
        stars.current = [];

        const pg = p.createGraphics(p.width, p.height);
        pg.pixelDensity(1);
        pg.background(0);

        // [Fix] 폰트 폴백 강화
        pg.textFont('Space Mono', 'Arial, sans-serif');
        pg.textStyle(p.BOLD);
        pg.textAlign(p.CENTER, p.CENTER);

        let fontSize = Math.min(p.width * 0.22, 500);
        if (p.width < 600) fontSize = p.width * 0.28;

        pg.textSize(fontSize);
        pg.fill(255);
        pg.text(CONFIG.text, p.width / 2, p.height / 2 - 40);

        pg.loadPixels();

        let step = 2;
        if (p.width < 800) step = 3;

        minX.current = p.width;
        maxX.current = 0;
        const tempPoints = [];

        // Scan pixels
        for (let y = 0; y < p.height; y += step) {
            for (let x = 0; x < p.width; x += step) {
                const index = (x + y * p.width) * 4;
                if (index < pg.pixels.length && pg.pixels[index] > 128) {
                    if (p.random(1) < 0.9) {
                        tempPoints.push(p.createVector(x, y));
                        if (x < minX.current) minX.current = x;
                        if (x > maxX.current) maxX.current = x;
                    }
                }
            }
        }
        pg.remove();

        // Center align adjustment
        if (tempPoints.length > 0) {
            const textBoundingCenter = (minX.current + maxX.current) / 2;
            const screenCenter = p.width / 2;
            const offsetX = screenCenter - textBoundingCenter;

            for (let pt of tempPoints) {
                pt.x += offsetX;
            }
            minX.current += offsetX;
            maxX.current += offsetX;
        }

        const maxP = p.width < 800 ? 1500 : 3500;
        const count = Math.min(tempPoints.length, maxP);

        for (let i = 0; i < count; i++) {
            const rndIdx = p.floor(p.random(tempPoints.length));
            const pt = tempPoints[rndIdx];
            tempPoints.splice(rndIdx, 1);

            const t = p.map(pt.x, minX.current, maxX.current, 0, 1);
            const c = getGradientColor(p, t);

            // Using the instance-safe factory
            particles.current.push(createGlowParticle(p, pt.x, pt.y, c));
        }

        for (let i = 0; i < CONFIG.starCount; i++) {
            stars.current.push(createStar(p));
        }

        console.log(`System Initialized: ${particles.current.length} particles, ${stars.current.length} stars`);
    };

    const getGradientColor = (p, t) => {
        const segments = CONFIG.colors.length - 1;
        let idx = p.floor(t * segments);
        let amt = (t * segments) - idx;
        if (idx < 0) idx = 0;
        if (idx >= segments) { idx = segments - 1; amt = 1; }

        const c1 = p.color(CONFIG.colors[idx]);
        const c2 = p.color(CONFIG.colors[idx + 1]);
        return p.lerpColor(c1, c2, amt);
    };

    const draw = (p) => {
        if (mode.current === 'warping') {
            p.background(3, 7, 18, 40);
        } else {
            p.clear();
        }

        if (!systemInitialized.current) return;

        const mouse = p.createVector(p.mouseX, p.mouseY);
        p.blendMode(p.ADD);

        p.push();
        p.translate(p.width / 2, p.height / 2);

        let targetSpeed = (mode.current === 'warping') ? CONFIG.starSpeedWarp : CONFIG.starSpeedBase;
        if (mode.current !== 'warping') {
            targetSpeed += p.map(p.mouseY, 0, p.height, 0, 1.5);
        }

        for (let star of stars.current) {
            star.update(targetSpeed);
            star.show();
        }
        p.pop();

        for (let part of particles.current) {
            part.behavior(mouse);
            part.update();
            part.show();
        }

        p.blendMode(p.BLEND);
    };

    // --- Particle Class Factory (Instance Safe) ---
    const createGlowParticle = (p, tx, ty, col) => {
        const target = p.createVector(tx, ty);
        const pos = p.createVector(p.random(p.width), p.random(p.height));
        const vel = p.createVector(0, 0);
        const acc = p.createVector(0, 0);
        const r = p.random(1.5, 3.5);
        const maxSpeed = 15;
        const maxForce = 0.9;
        const noiseOffset = p.createVector(p.random(1000), p.random(1000));
        const baseColor = col;

        // Helper to subtract vectors manually
        const subVector = (v1, v2) => p.constructor.Vector.sub(v1, v2);

        return {
            update: () => {
                pos.add(vel);
                vel.add(acc);
                acc.mult(0);
            },
            behavior: (mousePos) => {
                // Arrive Logic
                let desired = subVector(target, pos);
                let d = desired.mag();
                let speed = maxSpeed;
                if (d < 100) speed = p.map(d, 0, 100, 0, maxSpeed);
                desired.setMag(speed);
                let steer = subVector(desired, vel);
                steer.limit(maxForce);
                acc.add(steer);

                // Mouse Repel
                const dx = pos.x - mousePos.x;
                const dy = pos.y - mousePos.y;
                const distSq = dx * dx + dy * dy;
                const rSq = CONFIG.mouseRadius * CONFIG.mouseRadius;

                if (distSq < rSq) {
                    const f = p.createVector(dx, dy);
                    f.setMag(maxSpeed);
                    f.sub(vel);
                    f.limit(maxForce * 2.5);
                    f.mult(4); // Strength
                    acc.add(f);
                }

                if (mode.current === 'warping') {
                    const center = p.createVector(p.width / 2, p.height / 2);
                    const dir = subVector(pos, center);
                    dir.normalize();
                    dir.mult(p.random(30, 80));
                    acc.add(dir);
                } else {
                    const nX = (p.noise(noiseOffset.x + p.frameCount * 0.01) - 0.5) * 0.5;
                    const nY = (p.noise(noiseOffset.y + p.frameCount * 0.01) - 0.5) * 0.5;
                    acc.add(p.createVector(nX, nY));
                }
            },
            show: () => {
                p.strokeWeight(r * 2.5);
                p.stroke(p.red(baseColor), p.green(baseColor), p.blue(baseColor), 60);
                p.point(pos.x, pos.y);

                p.strokeWeight(r * 1.0);
                p.stroke(p.red(baseColor) + 100, p.green(baseColor) + 100, p.blue(baseColor) + 100, 255);
                p.point(pos.x, pos.y);
            }
        };
    };

    const createStar = (p) => {
        let x, y, z, pz;
        let color;

        const reset = () => {
            x = p.random(-p.width, p.width);
            y = p.random(-p.height, p.height);
            z = p.random(p.width, p.width * 2);
            pz = z;
            color = p.color(p.random(200, 255), p.random(230, 255), 255);
        };
        reset();
        z = p.random(p.width);

        return {
            update: (speed) => {
                z -= speed;
                if (z < 1) {
                    reset();
                    z = p.width;
                    pz = z;
                }
            },
            show: () => {
                const sx = p.map(x / z, 0, 1, 0, p.width);
                const sy = p.map(y / z, 0, 1, 0, p.height);
                const tailFactor = mode.current === 'warping' ? 2.5 : 0.6;
                const pz_calc = z + (pz - z) * tailFactor + (mode.current === 'warping' ? 50 : 10);
                const px = p.map(x / pz_calc, 0, 1, 0, p.width);
                const py = p.map(y / pz_calc, 0, 1, 0, p.height);

                let alpha = p.map(z, 0, p.width, 255, 0);
                if (mode.current !== 'warping' && p.random(1) < 0.05) alpha = p.random(150, 255);

                p.strokeWeight(p.map(z, 0, p.width, 2.5, 0.1));
                p.stroke(p.red(color), p.green(color), p.blue(color), alpha);
                p.line(px, py, sx, sy);
                pz = z;
            }
        };
    };

    const startSystem = () => {
        // [New] 인트로 방문 완료 플래그 설정
        localStorage.setItem('luna_intro_visited', 'true');

        mode.current = 'warping';
        setIsWarping(true);
        setTimeout(() => {
            navigate('/app');
        }, 1200);
    };

    return (
        <div className={`relative w-full h-screen bg-[#030712] overflow-hidden ${isWarping ? 'warp-transition' : ''}`}>

            <div className="bg-grid"></div>
            <div className="vignette"></div>

            {/* P5 Canvas */}
            <div className="absolute inset-0 z-[1]">
                <Sketch setup={setup} draw={draw} windowResized={windowResized} />
            </div>

            {/* UI Layer */}
            <div className={`absolute inset-0 z-10 flex flex-col justify-between items-center py-12 pointer-events-none transition-opacity duration-200 ${isWarping ? 'opacity-0' : 'opacity-100'}`}>

                {/* Top Badge */}
                <div className="flex items-center gap-2.5 mt-16 animate-fade-in-down" style={{ animationDelay: '2s' }}>
                    <div className="relative w-2 h-2">
                        <div className="absolute inset-0 bg-cyan-400 rounded-full shadow-[0_0_10px_#22d3ee]"></div>
                        <div className="absolute -inset-1 border border-cyan-400 rounded-full animate-ping-slow"></div>
                    </div>
                    <span className="font-mono text-[0.7rem] tracking-[0.2em] text-cyan-400/70 font-bold">SYSTEM ONLINE</span>
                </div>

                {/* Bottom Content */}
                <div className="flex flex-col items-center gap-8 mb-[8vh] pointer-events-auto w-full">

                    {/* Slogan */}
                    <div className="text-center px-5 animate-fade-in-up md:text-[2.2rem] text-[1.5rem]" style={{ animationDelay: '1.8s' }}>
                        <h1 className="korean-slogan">
                            당신의 코딩 논리를 깨우는,<br className="md:hidden" />
                            AI 학습 어드바이저
                        </h1>
                    </div>

                    {/* Acronym */}
                    <div className="flex items-center gap-6 animate-fade-in-up" style={{ animationDelay: '2.2s' }}>
                        <div className="w-10 h-px bg-gradient-to-r from-transparent to-cyan-400/50"></div>
                        <div className="font-mono text-sm text-white/60 tracking-[0.15em]">
                            <span><b className="text-cyan-400 drop-shadow-[0_0_10px_rgba(0,255,255,0.6)]">L</b>OGIC</span> &nbsp;
                            <span><b className="text-cyan-400 drop-shadow-[0_0_10px_rgba(0,255,255,0.6)]">U</b>NIFIED</span> &nbsp;
                            <span><b className="text-cyan-400 drop-shadow-[0_0_10px_rgba(0,255,255,0.6)]">N</b>EURAL</span> &nbsp;
                            <span><b className="text-cyan-400 drop-shadow-[0_0_10px_rgba(0,255,255,0.6)]">A</b>DVISOR</span>
                        </div>
                        <div className="w-10 h-px bg-gradient-to-r from-transparent to-cyan-400/50 transform scale-x-[-1]"></div>
                    </div>

                    {/* Button */}
                    <div className="animate-fade-in-up" style={{ animationDelay: '2.5s' }}>
                        <button onClick={startSystem} className="luna-btn-underline group">
                            INITIALIZE_WORKSPACE
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
}

// Helpers need to be patched into the component scope or passed p correctly.
// I pasted the factory functions inside the component body but before return.
// However, the `initSystem` used `createGlowParticle` which I renamed `createGlowParticleRefined`.
// I need to ensure initSystem calls the Refined version.
